/* eslint-disable @typescript-eslint/no-explicit-any */
import { withLayout } from '@/layout/Layout';
import { useEffect, useState } from 'react';
import {
  BreadcrumbsProps,
  PageProps,
  ReviewSchema,
  microMarkupSchemaProps,
} from '@/types/types';
import { LayoutProps } from '@/layout/Layout/Layout.props';
import { GetStaticPathsContext, GetStaticProps } from 'next';
import { getApolloClient } from '@/utils/apollo-client';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { transformMenus } from '@/utils/transformMenus';

import { ROUTES } from '@/utils/routes';
import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';
import { NEWS_BY_SLUG_QUERY } from '@/data/newsBySlugQuery';
import { ALL_NEWS_PATH } from '@/data/allNewsPath';
import { Section } from '@/components/common/Section';
import Image from 'next/image';
import { Heading } from '@/components/typography';
import { DateFormat } from '@/components/common/DateFormat';
import { Slider } from '@/components/common/Slider';
import { useTranslation } from 'next-i18next';
import { CommonCard } from '@/components/cards/CommonCard';
import { removeLastTrailingSlash } from '@/utils/removeLastTrailingSlash';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import { trimText } from '@/utils/trimText';
import { NEWS_SLUG_TRANSLATION } from '@/data/newsSlugTranslation';
import { PageHead } from '@/components/common/PageHead';
import { Comments } from '@/components/Comments';

const Slug = ({ data }: PageProps & LayoutProps): JSX.Element => {
  const [isMounted, setIsMounted] = useState(false);
  const { t } = useTranslation('common');
  const btnText = t('buttonMore');
  const anotherNewsTitle = t('anotherNewsTitle');
  const valueToStore = data?.newsBy?.translatedSlug || '';

  useEffect(() => {
    localStorage.setItem('translated_slug', valueToStore);
    return () => {
      localStorage.removeItem('translated_slug');
    };
  }, [valueToStore]);

  const {
    title,
    id,
    date,
    excerpt,
    content,
    featuredImage,
    newsSinglePage,
    seo: seoChild,
    commentStatus,
    commentCount,
    databaseId,
    comments,
  } = data?.newsBy?.translation ?? {};
  const { seo: seoParent } = data?.pageBy?.translation ?? {};

  const breadcrumbs = [
    ...(seoParent?.breadcrumbs ?? []),
    {
      text: seoChild?.breadcrumbTitle,
      url: seoChild?.canonicalUrl,
    },
  ] as BreadcrumbsProps[];

  const featureImage = featuredImage?.node;
  const otherNewsList = data?.allNews?.nodes
    .filter(item => item.id !== id)
    .slice(0, data?.allNews?.nodes.length > 3 ? 3 : undefined);

  const microMarkupSchema: microMarkupSchemaProps = {
    '@context': 'http://schema.org',
    '@type': 'Article',
    headline: title,
    image: featureImage.mediaItemUrl,
    datePublished: `${date}+03:00`,
    articleBody: content,
  };

  const schemaReviews: ReviewSchema[] =
    comments?.nodes && comments?.nodes?.length > 0
      ? comments?.nodes?.map(item => ({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: item.author.node.name,
          },
          datePublished: item.date,
          reviewBody: item.content,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: '5',
          },
        }))
      : [
          {
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: 'Guest',
            },
            datePublished: '2024-05-21 10:57:39',
            reviewBody: 'Great',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
            },
          },
        ];

  const reviewSchema: microMarkupSchemaProps = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name: title,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      reviewCount: commentCount ? commentCount.toString() : '1',
    },
    review: schemaReviews,
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seoChild?.breadcrumbTitle || title}
        seo={seoParent || {}}
        canonicalUrl={seoChild?.canonicalUrl}
        microMarkupSchema={microMarkupSchema}
        breadcrumbs={breadcrumbs}
        reviewSchema={reviewSchema}
        descriptionChild={seoChild?.description}
      />
      {isMounted && (
        <>
          <Section
            className="pb-[60px] md:pb-[100px]"
            breadcrumbs={breadcrumbs}
            lazy={false}
          >
            {featureImage && (
              <div className="mb-5 md:mb-[60px]">
                <Image
                  src={featureImage?.mediaItemUrl ?? '/images/image-bg.jpg'}
                  alt={featureImage?.altText ?? 'image not found'}
                  width={featureImage?.mediaDetails?.width ?? ''}
                  height={featureImage?.mediaDetails?.height ?? ''}
                  className="aspect-[2.25/1] w-full object-cover object-center md:aspect-[2.07/1] lg:aspect-[2.25/1]"
                />
              </div>
            )}
            <div className="mb-[100px] grid gap-y-[60px] last:mb-0 md:mb-[120px] md:grid-cols-[.3fr,_.7fr] md:gap-x-8 lg:grid-cols-2">
              {title && (
                <div className="border-b border-primaryLight pb-7 md:col-span-2 md:col-start-1 md:pb-10 md:pr-[30%] lg:col-span-1 lg:row-span-1 lg:self-start lg:pl-[20%] lg:pr-0">
                  <Heading
                    tag="h1"
                    color="accent-main"
                    content={title}
                    className="mb-10 text-h3Mobile md:mb-[60px] md:text-h1Mobile"
                  />
                  {date && (
                    <p className="dark:gray-scale-red-diff text-base text-accentMain md:text-lg">
                      <DateFormat date={date} />
                    </p>
                  )}
                </div>
              )}

              <div className="md:col-start-2 lg:row-span-2 lg:row-start-1">
                {excerpt && (
                  <div
                    dangerouslySetInnerHTML={{ __html: excerpt }}
                    className="mb-6 font-bold italic last:mb-0"
                  />
                )}
                {content && (
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                )}
              </div>

              {newsSinglePage?.postSidebarImages &&
                newsSinglePage?.postSidebarImages?.length > 0 && (
                  <ul className="grid gap-[62px] md:col-start-2 lg:col-start-1 lg:row-start-2 lg:h-full lg:content-around">
                    {newsSinglePage?.postSidebarImages?.map((item: any) => (
                      <li key={item.mediaItemUrl}>
                        <Image
                          src={item.mediaItemUrl ?? ''}
                          alt={item.altText ?? ''}
                          width={item.mediaDetails?.width}
                          height={item.mediaDetails?.height}
                        />
                      </li>
                    ))}
                  </ul>
                )}
            </div>
            {newsSinglePage?.newsGallery &&
              newsSinglePage?.newsGallery?.length > 0 && (
                <Slider
                  width={320}
                  withArrows
                  desktopShowSlider={3}
                  tabletShowSlider={2}
                  settings={{
                    autoplay: false,
                    infinite: newsSinglePage.newsGallery?.length > 3,
                  }}
                >
                  {newsSinglePage?.newsGallery?.map(item => (
                    <div
                      key={item.id}
                      className="block aspect-[1.6/1] object-center px-2"
                    >
                      {item.mediaItemUrl && (
                        <Image
                          src={item.mediaItemUrl}
                          alt={item.altText}
                          width={item?.mediaDetails?.width}
                          height={item?.mediaDetails?.height}
                          className="h-full w-full rounded object-cover object-center"
                        />
                      )}
                    </div>
                  ))}
                </Slider>
              )}
            {commentStatus === 'open' && (
              <Comments
                commentCount={commentCount}
                postId={databaseId}
                comments={comments}
                pt={
                  newsSinglePage?.newsGallery &&
                  newsSinglePage?.newsGallery?.length > 0
                    ? true
                    : false
                }
              />
            )}
          </Section>
          {otherNewsList && otherNewsList.length > 0 && (
            <Section className="py-10">
              <Heading withDivider content={anotherNewsTitle} />
              <Slider
                width={320}
                desktopShowSlider={
                  otherNewsList.length >= 3 ? 3 : otherNewsList.length
                }
                tabletShowSlider={2}
              >
                {otherNewsList.map(item => (
                  <div
                    key={item.id}
                    className={`p-3 md:h-full ${
                      otherNewsList.length < 3 && 'lg:max-w-[75%]'
                    }`}
                  >
                    <CommonCard
                      commonImage={item?.featuredImage?.node}
                      content={trimText(item.excerpt, 100)}
                      date={item?.date}
                      title={item?.title}
                      isInnerLink={true}
                      linkText={btnText}
                      isNews={true}
                      linkUrl={removeLastTrailingSlash(item?.slug)}
                      className="h-sliderMobile md:h-sliderTablet"
                    />
                  </div>
                ))}
              </Slider>
            </Section>
          )}
        </>
      )}
    </>
  );
};

export default withLayout(Slug);

//LABEL: PROPS
export const getStaticProps: GetStaticProps = async context => {
  const client = getApolloClient();

  const slug = `/${context.params?.slug}` ?? ROUTES.HOME;
  const language = context.locale?.toUpperCase();
  const allNewsLanguage = language;

  const location =
    context.locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;

  const slugLanguage = context.locale == 'uk' ? 'EN' : 'UK';

  const { data } = await client.query({
    query: NEWS_BY_SLUG_QUERY,
    variables: {
      slug,
      language,
      allNewsLanguage,
    },
  });

  const { data: translatedData } = await client.query({
    query: NEWS_SLUG_TRANSLATION,
    variables: {
      slug,
      language: slugLanguage,
    },
  });

  const translatedSlug =
    (translatedData?.newsBy?.translation?.slug as string) ?? '';

  const newData = {
    ...data,
    newsBy: {
      ...data.newsBy,
      translatedSlug,
    },
  };

  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });

  const footerLocation =
    context.locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    context.locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems?.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data: newData,
      menus,
      footerData,
      ...(await serverSideTranslations(context.locale as string, ['common'])),
    },
  };
};

// LABEL: PATHS
export const getStaticPaths = async ({ locales }: GetStaticPathsContext) => {
  const client = getApolloClient();

  const { data } = await client.query({
    query: ALL_NEWS_PATH,
  });

  const paths = data?.allNews?.nodes.flatMap(
    ({ slug, language }: { slug: string; language: { slug: string } }) => {
      return locales
        ?.filter(locale => locale === language.slug)
        .map(locale => ({
          params: {
            slug,
          },
          locale,
        }));
    },
  );

  return {
    paths,
    fallback: false,
  };
};
